import React, {useState} from 'react';
import {PieChart, Pie, BarChart, Cell, ResponsiveContainer, CartesianGrid, Tooltip, XAxis, YAxis, Legend, Bar, Label, LabelList} from 'recharts';
import Layout from '../../Shared/Layout';
import {Container, } from "@material-ui/core";
import i18n, { Trans } from "../../utils/i18n";
import classNames from "classnames";

function PieChartWrapper({ data01, data02, data03, COLORS, COLORS2, COLORS3 }) {
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart width={400} height={300}>
                <Pie
                    startAngle={-90}
                    endAngle={270}
                    data={data01} dataKey="value" cx="50%" cy="50%" innerRadius={70} outerRadius={90} fill="#FF4F50">
                    {data01.map((entry, index) => (
                        <Cell key={`cell1-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    label
                </Pie>
                <Pie
                    startAngle={-90}
                    endAngle={270}
                    data={data02} dataKey="value" cx="50%" cy="50%" innerRadius={100} outerRadius={120} fill="#175467">
                    {data02.map((entry, index) => (
                        <Cell key={`cell2-${index}`} fill={COLORS2[index % COLORS2.length]} />
                    ))}
                </Pie>
                <Pie
                    startAngle={-90}
                    endAngle={270}
                    data={data03} dataKey="value" cx="50%" cy="50%" innerRadius={130} outerRadius={150} fill="#175467">
                    {data03.map((entry, index) => (
                        <Cell key={`cell3-${index}`} fill={COLORS3[index % COLORS3.length]} />
                    ))}
                </Pie>
            </PieChart>
        </ResponsiveContainer>
    );
}

export function formatUnit(value, unit = '') {
    if(unit === 'kg' && value > 1000) {
        value = value / 1000;
        unit = 't';
    }
    if(unit === 'balloons' && value > 10000) {
        value = value / 1000;
        unit = 'k balloons';
    }
    value = value.toLocaleString('en-US', { maximumFractionDigits: 0, });
    return `${value} ${unit}`;
}

export default function AvoidedEmissions({ result, isEnterprise, }) {
    const entry = result.models[0];
    const device = entry.product;
    const coEmitReplacement = Math.round(result.total_device_pcf); // Math.round(entry.sourcing_and_production + entry.transport + entry.eol);
    const coEmitReplacementKg = formatUnit(Math.round(result.total_device_pcf), 'kg');
    const coEmitRepair =  Math.round(result.repair_emission );
    const coFactor =  Math.round(coEmitReplacement / coEmitRepair );
    const avoidedEmission = coEmitReplacement - coEmitRepair;
    const fault = entry.problem;
    const km_of_car_ride = formatUnit(result.km_of_car_ride,'km');
    const balloons = formatUnit(result.balloons, 'balloons');
    const trees_compensation = formatUnit(result.trees_compensation);
    const avoided_emission = formatUnit(result.avoided_emission, 'kg');
    const trees_text = trees_compensation == 1 ? 'tree' : 'trees';

    const data = [
        {
            name: 'Repair',
            labelRepair: coEmitRepair + ' kg repair emissions',
            labelAvoided: avoidedEmission + ' kg avoided emissions',
            Repair: coEmitRepair,
            AvoidedEmission: avoidedEmission,
            // Replacement: 0, // Add a placeholder value for Replacement
        },
        {
            name: 'Replacement',
            // Repair: 0, // Add a placeholder value for Repair
            // AvoidedEmission: 0, // Add a placeholder value for AvoidedEmission
            Replacement: coEmitReplacement,
            labelReplacement: coEmitReplacement + ' kg replacement emissions',
        },
    ];


    return (
        <>
            {
                coFactor > 1 && (
                    <p className={'m-0 text-3xl font-bold text-black'}>
                        <Trans i18nKey="carbon.yourCo2Footprint" coFactor={coFactor}>
                            A replacement causes <span style={{
                            background: "linear-gradient(90deg, #FF4F50 0%, #F13B5F 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            fontSize: 50,
                            marginRight: 10,
                        }}>{{ coFactor }} </span>
                            times more CO2e than repairing your device.
                        </Trans>
                    </p>

                )
            }
            {
                coFactor <= 1 && (
                    <p className={'m-0 text-3xl font-bold text-black'}>
                        <Trans i18nKey="carbon.repairLower">
                            A replacement has a much higher CO2 footprint than a repair.
                        </Trans>
                    </p>

                )
            }
            <p className={'m-0 text-lg mt-3 font-medium'}>
                <Trans i18nKey="carbon.calculationExplanation">
                    Below, you'll find a detailed breakdown of your emissions. When it comes to repairing your device, emissions are generated at various stages, including transportation, the repair operation, and the production of new replacement parts. In contrast, opting to replace your device with a new one would result in a larger emissions footprint due to the procurement of raw materials, the manufacturing process, and the distribution from the factory to the final point of use.
                </Trans>
            </p>
            <div className={'w-full h-[200px] mt-20'}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={800}
                        height={500}
                        data={data}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 40,
                            bottom: 5
                        }}
                        layout={'vertical'}
                    >
                        <defs>
                            <linearGradient id="colorPv" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#FF4F50" stopOpacity={1} />
                                <stop offset="100%" stopColor="#CE0A7F" stopOpacity={1} />
                            </linearGradient>
                            <linearGradient id="colorB2X" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#ff5050" stopOpacity={1} />
                                <stop offset="100%" stopColor="#ce097f" stopOpacity={1} />
                            </linearGradient>
                            <linearGradient id="colorGreen" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#138748" stopOpacity={1} />
                                <stop offset="100%" stopColor="#59C665" stopOpacity={1} />
                            </linearGradient>
                        </defs>
                        <XAxis type={'number'} />
                        <YAxis type="category" dataKey="name" />
                        {/* <Tooltip /> */}
                        {/*<Legend wrapperStyle={{ color: 'black' }} />*/}
                        <Bar isAnimationActive={false} dataKey="Repair" stackId="a" fill="url(#colorB2X)" radius={[30, 30, 30, 30]}>
                            <LabelList dataKey="labelRepair" position="inside" fill="#fff"
                                       style={{ fontSize: '18px' }}  />
                        </Bar>
                        <Bar isAnimationActive={false} dataKey="AvoidedEmission" stackId="a" fill="url(#colorGreen)" radius={[30, 30, 30, 30]}>
                            <LabelList dataKey="labelAvoided" position="inside" fill="#fff"
                                       style={{ fontSize: '18px' }}  />
                        </Bar>
                        <Bar isAnimationActive={false} dataKey="Replacement" stackId="a" fill="url(#colorB2X)" radius={[30, 30, 30, 30]}>
                            <LabelList dataKey="labelReplacement" position="inside" fill="#fff"
                                       style={{ fontSize: '18px' }}  />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </>
    )
}


AvoidedEmissions.layout = page => <Layout children={page} />;
